import classnames from 'classnames';
import { ENV_VARS } from 'ENV_CONFIG';
import { PrimaryButton, SecondaryButton } from 'jl-design-system/elements/button/Button';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import { connect } from 'react-redux';
import { updateDataLayerWithBookingId } from '~/analytics/analytics.utils';
import { appointmentDraftApi, bookingsActions, bookingsSelectors, tokenApi } from '~/api';
import {
  apiCallFinished,
  BookingReview as DefaultBookingReview,
  envConfigSelectors,
  GoogleMap,
  questionsActions,
  withProgressBar,
} from '~/components';
import { isEmpty } from '~/utils';
import { willBookingBeWithinPeriod } from '../utils';
import { BookingInformation } from './booking-information/BookingInformation';
import styles from './CompletePage.scss';
import { SuccessBookingMessageContainer } from './success-booking-message';

const getRef = (path) => `${ENV_VARS.REACT_APP_BROWSE_HOST}${path}`;

export class CompletePage extends PureComponent {
  state = {
    isAjaxCompleted: false,
    onAnotherAppointmentUrl: getRef('/our-services'),
  };

  componentDidMount() {
    const {
      match: {
        params: { bookingId },
      },
      dispatchRetrieveQuestions,
      dispatchSetBooking,
      dispatchApiCallFinished,
    } = this.props;

    dispatchApiCallFinished(false);
    dispatchSetBooking(bookingId)
      .then((booking) => {
        dispatchRetrieveQuestions(booking.items[0].appointmentType.id).then(() => {
          dispatchApiCallFinished(true);
          updateDataLayerWithBookingId(booking.id);
        });
      })
      .catch(this.handleCreateAppointmentError);
    window.scrollTo(0, 0);
  }

  componentDidUpdate(prevProps) {
    const { store: prevStore } = prevProps;
    const { store } = this.props;
    if (store !== prevStore) {
      this.getOnAnotherAppointmentUrl().then((onAnotherAppointmentUrl) => {
        this.setState({ isAjaxCompleted: true });
        this.setState({
          onAnotherAppointmentUrl,
        });
      });
    }
  }

  handleCreateAppointmentError = (response) => {
    const { status } = response;
    if (status !== 410) {
      Promise.reject(response);
    }
  };

  getOnAnotherAppointmentUrl = () => {
    const {
      store,
      config: { bookAnotherAppointmentLink },
    } = this.props;

    if (bookAnotherAppointmentLink) {
      return tokenApi
        .getInitialToken()
        .then((initialToken) => this.setNecessaryDataToToken(initialToken, store.id))
        .then(({ token }) => `${bookAnotherAppointmentLink.ref(store.shortName)}?token=${token}`);
    }
    return Promise.resolve(getRef('/our-services'));
  };

  setNecessaryDataToToken = (token, storeId) =>
    appointmentDraftApi.updateAppointmentDraft({
      token,
      storeId,
    });

  render() {
    const {
      bookingItems,
      mainBookingItem,
      store,
      bookingText,
      config,
      serviceConfig,
      isRemoteV1,
      isRemoteV2,
      isRemote,
      gender,
      appointmentInformation,
      remoteConfig,
      hideShopDetails,
      hideShopAddress,
      shopDetails,
      hidePartnerDetails,
      hideAddToCalendarButton,
      hidePriceDetails,
      displayPropertyAddress,
      formatBookingAddressFunc,
      hideManageAppointmentsButton,
      hidePhoneSection,
    } = this.props;
    const { isAjaxCompleted, onAnotherAppointmentUrl } = this.state;

    if (bookingItems.length === 0 || isEmpty(config)) {
      return null;
    }

    const {
      bookAnotherAppointmentLink = {
        title: 'Book another appointment',
      },
      BookingReview: bookingReviewConfig = {},
      ItemListDetails: itemsListDetailsConfig = {},
    } = config;

    const BookingReview = bookingReviewConfig.BookingReviewComponent || DefaultBookingReview;
    const ItemsListDetails =
      itemsListDetailsConfig.component || bookingReviewConfig.TreatmentListDetailsComponent;
    const TotalPriceDetails = bookingReviewConfig.TotalPriceDetailsComponent;

    return (
      <div className={styles.CompletePageContainer}>
        <SuccessBookingMessageContainer within48h={willBookingBeWithinPeriod(mainBookingItem)} />
        <section
          data-testid="CompletePage"
          className={styles.CompletePage}
          data-test-ajax={isAjaxCompleted}
        >
          <div className={styles.BookingHolder}>
            <BookingReview
              booking={mainBookingItem}
              config={config}
              remote={remoteConfig}
              serviceConfig={serviceConfig}
              isRemoteV1={isRemoteV1}
              isRemoteV2={isRemoteV2}
              hideShopDetails={hideShopDetails}
              hideShopAddress={hideShopAddress}
              shopDetails={shopDetails}
              hidePartnerDetails={hidePartnerDetails}
              hideAddToCalendarButton={hideAddToCalendarButton}
              hidePriceDetails={hidePriceDetails}
              formatBookingAddressFunc={formatBookingAddressFunc}
              hidePhoneSection={hidePhoneSection}
            />
          </div>
          {hideManageAppointmentsButton || !!ItemsListDetails || (
            <div className={styles.Buttons}>
              <a
                href={`${ENV_VARS.REACT_APP_BROWSE_HOST}/appointments/manage`}
                className={styles.ManageAppointments}
              >
                <PrimaryButton inline>Manage your appointment</PrimaryButton>
              </a>
            </div>
          )}
          {!displayPropertyAddress && !bookingReviewConfig.displayUserAddress && !isRemote && (
            <GoogleMap location={store.address.location} storeName={store.name} />
          )}
          {!!ItemsListDetails && (
            <ItemsListDetails
              sectionTitle={itemsListDetailsConfig.sectionTitle}
              showEditButton={false}
            />
          )}
          {!!TotalPriceDetails && <TotalPriceDetails />}

          <div
            className={classnames(styles.BookingInformation, {
              [styles.ResetBottomIndent]: !hideManageAppointmentsButton && !!ItemsListDetails,
            })}
          >
            <BookingInformation
              appointmentInformation={appointmentInformation}
              gender={gender}
              bookingText={bookingText}
            />
            {!hideManageAppointmentsButton && ItemsListDetails && (
              <div className={styles.ManageAppointmentsWrapper}>
                <a
                  href={`${ENV_VARS.REACT_APP_BROWSE_HOST}/appointments/manage`}
                  className={styles.ManageAppointments}
                >
                  <PrimaryButton inline>Manage your appointment</PrimaryButton>
                </a>
              </div>
            )}
          </div>
        </section>
        <div className={styles.Buttons}>
          <a href={onAnotherAppointmentUrl} className={styles.BookAppointment}>
            <SecondaryButton inline>{bookAnotherAppointmentLink.title}</SecondaryButton>
          </a>
          <a href={ENV_VARS.REACT_APP_BROWSE_HOST} className={styles.ContinueShopping}>
            <SecondaryButton inline>Continue shopping</SecondaryButton>
          </a>
        </div>
      </div>
    );
  }
}

export const mapStateToProps = (state) => {
  const appointmentTypeId = bookingsSelectors.selectAppointmentTypeId(state);

  return {
    bookingItems: bookingsSelectors.selectBookingItems(state),
    store: bookingsSelectors.selectStore(state),
    bookingText: bookingsSelectors.selectBookingText(state),
    mainBookingItem: bookingsSelectors.selectMainBookingItem(state),
    config: envConfigSelectors.selectConfig(appointmentTypeId)(state),

    serviceConfig: envConfigSelectors.serviceConfig(appointmentTypeId)(state),
    isRemote: envConfigSelectors.isAppointmentTypeRemote(appointmentTypeId)(state),
    isRemoteV1: envConfigSelectors.isAppointmentTypeRemoteV1(appointmentTypeId)(state),
    isRemoteV2: envConfigSelectors.isAppointmentTypeRemoteV2(appointmentTypeId)(state),
    gender: envConfigSelectors.selectAppointmentTypeGender(appointmentTypeId)(state),
    appointmentInformation:
      envConfigSelectors.selectBookingAppointmentInformation(appointmentTypeId)(state),
    remoteConfig: envConfigSelectors.remoteConfig(appointmentTypeId)(state),
    shopDetails: envConfigSelectors.bookingReviewShopDetails(appointmentTypeId)(state),
    hideShopDetails:
      envConfigSelectors.selectHideShopDetailsOnCompletePage(appointmentTypeId)(state),
    hideShopAddress: envConfigSelectors.hideShopAddressOnCompletePage(appointmentTypeId)(state),
    hidePartnerDetails:
      envConfigSelectors.selectHidePartnerDetailsOnCompletePage(appointmentTypeId)(state),
    hideAddToCalendarButton:
      envConfigSelectors.selectHideAddToCalendarButtonOnCompletePage(appointmentTypeId)(state),
    hidePriceDetails:
      envConfigSelectors.selectHidePriceDetailsOnCompletePage(appointmentTypeId)(state),
    formatBookingAddressFunc:
      envConfigSelectors.selectFormatBookingAddressFuncOnCompletePage(appointmentTypeId)(state),
    displayPropertyAddress:
      envConfigSelectors.selectDisplayPropertyAddressOnCompletePage(appointmentTypeId)(state),
    hideManageAppointmentsButton:
      envConfigSelectors.selectHideManageAppointmentsButtonOnCompletePage(appointmentTypeId)(state),
    hidePhoneSection:
      envConfigSelectors.selectHidePhoneSectionOnCompletePage(appointmentTypeId)(state),
  };
};

const mapDispatchToProps = {
  dispatchApiCallFinished: apiCallFinished,
  dispatchSetBooking: bookingsActions.setBooking,
  dispatchRetrieveQuestions: questionsActions.retrieveQuestions,
};

export const ConnectedCompletePage = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withProgressBar(CompletePage));

CompletePage.propTypes = {
  config: PropTypes.shape({}),
  remoteConfig: PropTypes.shape({}),
  bookingItems: PropTypes.arrayOf(PropTypes.shape({})),
  mainBookingItem: PropTypes.shape({}),
  store: PropTypes.shape({
    id: PropTypes.string.isRequired,
    shortName: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    address: PropTypes.shape({
      location: PropTypes.shape({
        latitude: PropTypes.number.isRequired,
        longitude: PropTypes.number.isRequired,
      }).isRequired,
    }).isRequired,
  }),
  bookingText: PropTypes.string,
  serviceConfig: PropTypes.shape({}),
  isRemoteV1: PropTypes.bool,
  isRemoteV2: PropTypes.bool,
  isRemote: PropTypes.bool,
  match: PropTypes.shape({}).isRequired,
  gender: PropTypes.string,
  appointmentInformation: PropTypes.func,

  dispatchApiCallFinished: PropTypes.func.isRequired,
  dispatchSetBooking: PropTypes.func.isRequired,
  hideShopDetails: PropTypes.bool,
  hideShopAddress: PropTypes.bool,
  shopDetails: PropTypes.shape({
    title: PropTypes.string,
  }),
  hidePartnerDetails: PropTypes.bool,
  hideAddToCalendarButton: PropTypes.bool,
  hidePriceDetails: PropTypes.bool,
  displayPropertyAddress: PropTypes.bool,
  hideManageAppointmentsButton: PropTypes.bool,
  formatBookingAddressFunc: PropTypes.func,
  dispatchRetrieveQuestions: PropTypes.func.isRequired,
  hidePhoneSection: PropTypes.bool,
};

CompletePage.defaultProps = {
  config: {},
  remoteConfig: {},
  bookingItems: [],
  mainBookingItem: null,
  store: null,
  bookingText: null,
  serviceConfig: null,
  isRemoteV1: false,
  isRemoteV2: false,
  isRemote: false,
  gender: null,
  appointmentInformation: null,
  hideShopDetails: false,
  hideShopAddress: false,
  shopDetails: {
    title: 'Shop',
  },
  hidePartnerDetails: false,
  hideAddToCalendarButton: false,
  hidePriceDetails: false,
  displayPropertyAddress: false,
  formatBookingAddressFunc: (param) => param,
  hideManageAppointmentsButton: false,
  hidePhoneSection: false,
};
